/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Database, Image as ImageIcon, Palette, Receipt, CheckCircle2, ChevronRight, ChevronLeft, Loader2, Sparkles, Server,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { applyBrandTheme } from '@/lib/brandTheme';
import { useBrand } from '@/lib/whitelabel';
import { THEME_CONFIG } from '@/config/themeConfig';
import {
  setupStatus, setupLogin, setupTestDb, setupLogo, setupBrand, setupCreateAdmin, setSetupToken, getSetupToken,
} from '@/lib/setup';
import { SetupGate } from '@/components/setup/SetupGate';
import { SetupStepper } from '@/components/setup/SetupStepper';
import { StepDatabase } from '@/components/setup/StepDatabase';
import { StepLogo } from '@/components/setup/StepLogo';
import { StepBrand } from '@/components/setup/StepBrand';
import { StepPricing } from '@/components/setup/StepPricing';
import { StepDone } from '@/components/setup/StepDone';
import { serializeTiers } from '@/components/brand/IseeTiersEditor';
import { ProprietaryWatermark } from '@/components/security/ProprietaryWatermark';

/**
 * SetupWizardPage — /setup
 * Installer guidato per l'amministratore IT della clinica (protetto da password).
 *  0 Password  → 1 Database → 2 Logo → 3 Palette & dati → 4 Listino → 5 Riepilogo
 * Al termine salva su DB e genera `brand-config.json` (download + scrittura su
 * disco quando possibile) e ricarica il brand in tutta la UI senza rebuild.
 *
 * La UI dei singoli passi vive in `components/setup/*`; qui resta solo la
 * macchina a stati e le chiamate API.
 */

const STEPS = [
  { id: 'db', label: 'Database', icon: Database },
  { id: 'logo', label: 'Logo', icon: ImageIcon },
  { id: 'brand', label: 'Palette & dati', icon: Palette },
  { id: 'pricing', label: 'Listino', icon: Receipt },
  { id: 'done', label: 'Riepilogo', icon: CheckCircle2 },
];
const STEP_DONE = STEPS.length - 1;
const STEP_PRICING = STEP_DONE - 1;

const RESULT_KEY = 'sanitanexus.setup.result';
const readStoredResult = () => { try { return JSON.parse(sessionStorage.getItem(RESULT_KEY) || 'null'); } catch { return null; } };
const readFileAsDataUrl = (file) => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });

const INITIAL_FORM = {
  clinicName: '', clinicShortName: '', tagline: THEME_CONFIG.tagline,
  primaryColor: THEME_CONFIG.colors.primary, secondaryColor: THEME_CONFIG.colors.secondary, accentColor: THEME_CONFIG.colors.accent,
  contactEmail: '', contactPhone: '', legalOwner: '', legalAddress: '', legalVat: '', legalFiscalCode: '', legalDpo: '',
};
const { currency: _cur, items: _items, ...INITIAL_PRICING } = THEME_CONFIG.priceList;

export default function SetupWizardPage() {
  const navigate = useNavigate();
  const brandCtx = useBrand();
  const [status, setStatus] = useState(null);
  const [authed, setAuthed] = useState(!!getSetupToken());
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  // Se il wizard ha appena scritto brand-config.json il dev-server puo' ricaricare la pagina:
  // ripristiniamo il riepilogo finale dallo storage di sessione.
  const stored = readStoredResult();
  const [step, setStep] = useState(stored ? STEP_DONE : 0);

  const [dbUrl, setDbUrl] = useState('');
  const [dbResult, setDbResult] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoUploaded, setLogoUploaded] = useState(false);
  const [form, setForm] = useState(INITIAL_FORM);
  const [pricing, setPricing] = useState(INITIAL_PRICING);
  const [isee, setIsee] = useState(THEME_CONFIG.iseeTiers);
  const [iseeEnabled, setIseeEnabled] = useState(true);
  const [admin, setAdmin] = useState({ email: '', password: '', full_name: '' });
  const [result, setResult] = useState(stored);

  useEffect(() => { setupStatus().then(setStatus).catch(() => setStatus({ error: true })); }, []);

  // Anteprima live della palette su tutta la pagina
  useEffect(() => {
    if (authed) applyBrandTheme({ primaryColor: form.primaryColor, secondaryColor: form.secondaryColor, accentColor: form.accentColor });
  }, [form.primaryColor, form.secondaryColor, form.accentColor, authed]);

  const handleUnauthorized = (err) => {
    if (err?.response?.status === 401) { setSetupToken(null); setAuthed(false); }
  };

  const login = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    try {
      const r = await setupLogin(password);
      setSetupToken(r.token);
      setAuthed(true);
      toast.success('Accesso al wizard consentito (30 minuti)');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Password non valida');
    } finally { setBusy(false); }
  };

  const testDb = async () => {
    setBusy(true); setDbResult(null);
    try { setDbResult(await setupTestDb(dbUrl.trim())); } catch (err) {
      handleUnauthorized(err);
      setDbResult({ ok: false, error: err?.response?.data?.detail || err.message });
    } finally { setBusy(false); }
  };

  const onLogo = async (file) => {
    if (!file) return;
    if (file.size > 1024 * 1024) { toast.error('Logo troppo grande (max 1 MB)'); return; }
    const du = await readFileAsDataUrl(file);
    setLogoPreview(du);
    setBusy(true);
    try { await setupLogo(du); setLogoUploaded(true); toast.success('Logo caricato'); } catch (err) { toast.error(err?.response?.data?.detail || 'Upload non riuscito'); } finally { setBusy(false); }
  };

  const applyPreset = (p) => setForm((f) => ({ ...f, primaryColor: p.primaryColor, secondaryColor: p.primaryColorAlt, accentColor: p.accentColor, tagline: f.tagline || p.taglineSuggestion }));

  const finish = async () => {
    if (!form.clinicName.trim()) { toast.error('Inserisci il nome della clinica'); setStep(2); return; }
    setBusy(true);
    try {
      const body = {
        ...Object.fromEntries(Object.entries(form).filter(([, v]) => v !== '' && v !== null)),
        priceList: { currency: THEME_CONFIG.priceList.currency, ...Object.fromEntries(Object.entries(pricing).map(([k, v]) => [k, Number(v)])), items: [] },
        iseeTiers: iseeEnabled ? serializeTiers(isee) : [],
      };
      const r = await setupBrand(body);
      if (admin.email && admin.password) {
        try { await setupCreateAdmin(admin); toast.success('Amministratore creato'); } catch (err) { toast.error(err?.response?.data?.detail || 'Admin non creato'); }
      }
      try {
        sessionStorage.setItem(RESULT_KEY, JSON.stringify(r));
      } catch (storageErr) {
        // Storage pieno o non disponibile: il risultato resta comunque in memoria (state).
        if (process.env.NODE_ENV !== 'production') console.debug('[SetupWizard] sessionStorage non disponibile', storageErr);
      }
      setResult(r);
      setStep(STEP_DONE);
      brandCtx.reload?.();
      toast.success('Configurazione salvata: brand applicato a tutta la UI');
    } catch (err) {
      handleUnauthorized(err);
      toast.error(err?.response?.data?.detail || 'Salvataggio non riuscito');
    } finally { setBusy(false); }
  };

  const downloadConfig = () => {
    if (!result?.brand_config) return;
    const blob = new Blob([JSON.stringify(result.brand_config, null, 2)], { type: 'application/json' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'brand-config.json'; a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1500);
  };

  const goApp = () => { setSetupToken(null); sessionStorage.removeItem(RESULT_KEY); navigate('/login'); };

  const canNext = useMemo(() => (step === 2 ? !!form.clinicName.trim() : true), [step, form.clinicName]);

  if (!authed) {
    return <SetupGate status={status} password={password} onPasswordChange={setPassword} onSubmit={login} busy={busy} onBack={() => navigate('/welcome')} />;
  }

  return (
    <div className="min-h-screen bg-background p-4 md:p-8 relative isolate" data-testid="setup-wizard">
      <ProprietaryWatermark />
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-primary text-primary-foreground flex items-center justify-center"><Sparkles className="h-5 w-5" /></div>
            <div>
              <h1 className="font-display font-bold text-xl">Installer Wizard — {form.clinicName || result?.brand_config?.clinicName || 'La tua clinica'}</h1>
              <p className="text-xs text-muted-foreground">Zero codice: tutto ciò che configuri qui finisce in <span className="font-mono">brand-config.json</span>.</p>
            </div>
          </div>
          <Badge variant="outline" className="text-[10px]"><Server className="h-3 w-3 mr-1" /> {status?.db_kind || 'db'}</Badge>
        </div>

        <SetupStepper steps={STEPS} step={step} onStepChange={setStep} />

        <Card className="rounded-xl">
          <CardContent className="p-5 md:p-6 space-y-5">
            {step === 0 && <StepDatabase dbUrl={dbUrl} onDbUrlChange={setDbUrl} onTest={testDb} busy={busy} result={dbResult} currentDbKind={status?.db_kind} />}
            {step === 1 && <StepLogo preview={logoPreview} uploaded={logoUploaded} onFile={onLogo} />}
            {step === 2 && <StepBrand form={form} onChange={setForm} onPreset={applyPreset} />}
            {step === STEP_PRICING && (
              <StepPricing pricing={pricing} onPricingChange={setPricing} isee={isee} onIseeChange={setIsee} iseeEnabled={iseeEnabled} onIseeEnabledChange={setIseeEnabled} admin={admin} onAdminChange={setAdmin} />
            )}
            {step === STEP_DONE && <StepDone result={result} onDownload={downloadConfig} onGoApp={goApp} />}

            {step < STEP_DONE && (
              <div className="flex items-center justify-between pt-2 border-t border-border/70">
                <Button variant="ghost" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} className="h-11 rounded-xl" data-testid="setup-prev"><ChevronLeft className="h-4 w-4" /> Indietro</Button>
                {step < STEP_PRICING ? (
                  <Button onClick={() => setStep((s) => s + 1)} disabled={!canNext} className="h-11 rounded-xl" data-testid="setup-next">Avanti <ChevronRight className="h-4 w-4" /></Button>
                ) : (
                  <Button onClick={finish} disabled={busy} className="h-11 rounded-xl" data-testid="setup-finish">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Salva e genera brand-config.json</Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
